@if (displayLoading) {
  <div
    class="d-flex flex-column align-items-center justify-content-center common-loading-container"
  >
    <div class="w-25 text-center">
      <img class="common-loading" src="assets/menu_loading.gif" alt="common loading" />
      <p class="common-loading-text">Loading reservation settings...</p>
    </div>
  </div>
} @else {
  <form [formGroup]="form" class="rs-page" (ngSubmit)="save()">
    <div class="rs-card">
      <div class="rs-heading">
        <div>
          <span class="rs-title">Reservation settings</span>
          <span class="rs-subtitle"
            >Choose how your restaurant takes bookings and the rules the floor follows.</span
          >
        </div>
        <span class="rs-heading-actions">
          <button
            type="button"
            class="rs-help"
            (click)="startTour()"
            matTooltip="How these settings work"
            aria-label="Take the tour of the reservation settings"
            data-tour="help"
          >
            <i class="bx bx-help-circle" aria-hidden="true"></i>
          </button>
          <a class="rs-link" routerLink="/main/reservation" data-tour="book"
            ><i class="bx bx-book-open" aria-hidden="true"></i> Open the book</a
          >
        </span>
      </div>

      <!-- Mode -->
      <div class="rs-section" data-tour="mode">
        <span class="rs-section-label">Reservation mode</span>
        <div class="rs-modes" role="radiogroup" aria-label="Reservation mode">
          @for (m of modes; track m.value) {
            <button
              type="button"
              role="radio"
              class="rs-mode"
              [class.rs-mode--on]="mode === m.value"
              [attr.aria-checked]="mode === m.value"
              (click)="setMode(m.value)"
            >
              <span class="rs-mode-icon"
                ><i class="bx" [ngClass]="m.icon" aria-hidden="true"></i
              ></span>
              <span class="rs-mode-title">{{ m.title }}</span>
              <span class="rs-mode-desc">{{ m.description }}</span>
            </button>
          }
        </div>
        <p class="rs-hint">
          <i class="bx bx-info-circle" aria-hidden="true"></i>
          The platform-wide <strong>RESERVATIONS</strong> feature flag must also be on for this
          restaurant; this mode is your own opt-in.
        </p>
        @if (!showsGuestSettings) {
          <p class="rs-hint rs-hint--off" data-testid="off-note">
            <i class="bx bx-bell-off" aria-hidden="true"></i>
            Pick a mode to set up guest notifications and booking channels.
          </p>
        }
      </div>

      @if (showsTimed) {
        <!-- Availability rules -->
        <div class="rs-section" data-tour="availability">
          <span class="rs-section-label">Availability</span>

          <div class="rs-row">
            <span class="rs-icon"><i class="bx bx-grid-small"></i></span>
            <div class="rs-text">
              <span class="rs-row-title">Slot interval</span>
              <span class="rs-row-desc">How far apart bookable times are offered.</span>
            </div>
            <div class="rs-field">
              <input
                type="number"
                class="rs-input"
                formControlName="slotIntervalMinutes"
                min="5"
                max="60"
                step="5"
              /><span class="rs-unit">min</span>
            </div>
          </div>

          <div class="rs-row rs-row--stack">
            <span class="rs-icon"><i class="bx bx-timer"></i></span>
            <div class="rs-text">
              <span class="rs-row-title">Turn times by party size</span>
              <span class="rs-row-desc"
                >How long a table is held for a party. The engine uses these to find free
                tables.</span
              >
              <div class="rs-bands" formArrayName="turnTimes">
                <!-- Track each row by its control: by index, removing a row would
                     drop the last row's DOM and leave the others on stale controls. -->
                @for (band of turnTimes.controls; track band; let i = $index) {
                  <div class="rs-band" [formGroup]="asGroup(band)">
                    <span class="rs-band-label">up to</span>
                    <input
                      type="number"
                      class="rs-input rs-input--sm"
                      formControlName="maxParty"
                      min="1"
                      max="99"
                      aria-label="Max party"
                    />
                    <span class="rs-band-label">guests →</span>
                    <input
                      type="number"
                      class="rs-input rs-input--sm"
                      formControlName="minutes"
                      min="15"
                      max="360"
                      step="5"
                      aria-label="Turn time minutes"
                    />
                    <span class="rs-unit">min</span>
                    <button
                      type="button"
                      class="rs-x"
                      (click)="removeBand(i)"
                      [disabled]="turnTimes.length === 1"
                      aria-label="Remove band"
                    >
                      <i class="bx bx-x"></i>
                    </button>
                  </div>
                }
                <button type="button" class="rs-link" (click)="addBand()">
                  <i class="bx bx-plus"></i> Add band
                </button>
              </div>
            </div>
          </div>

          <div class="rs-row" formGroupName="pacing">
            <span class="rs-icon"><i class="bx bx-tachometer"></i></span>
            <div class="rs-text">
              <span class="rs-row-title">Pacing cap</span>
              <span class="rs-row-desc"
                >Most covers that may start in one interval, so the kitchen is not swamped.</span
              >
            </div>
            <div class="rs-field">
              <input
                type="number"
                class="rs-input"
                formControlName="maxCoversPerInterval"
                min="1"
                max="500"
              /><span class="rs-unit">covers</span>
            </div>
          </div>

          <div class="rs-row rs-row--stack" formGroupName="party">
            <span class="rs-icon"><i class="bx bx-group"></i></span>
            <div class="rs-text">
              <span class="rs-row-title">Party size</span>
              <span class="rs-row-desc">
                @if (mode === 'request') {
                  Online bookings outside this range are refused.
                } @else {
                  Online bookings outside this range are refused; larger parties above the
                  auto-confirm limit arrive as requests.
                }
              </span>
              <div class="rs-inline">
                <label
                  >Min
                  <input type="number" class="rs-input rs-input--sm" formControlName="min" min="1"
                /></label>
                <label
                  >Max
                  <input
                    type="number"
                    class="rs-input rs-input--sm"
                    formControlName="max"
                    min="1"
                    max="100"
                /></label>
                <!-- Request to book makes every booking a request, so there is
                     nothing to auto-confirm (FNF-308). Hidden only: the value
                     stays in the form and comes back with timed or hybrid. -->
                @if (mode !== 'request') {
                  <label
                    >Auto-confirm up to
                    <input
                      type="number"
                      class="rs-input rs-input--sm"
                      formControlName="autoConfirmUpTo"
                      min="0"
                      max="100"
                  /></label>
                }
              </div>
              @if (mode === 'request') {
                <p class="rs-hint" data-testid="request-mode-hint">
                  <i class="bx bx-message-square-check" aria-hidden="true"></i>
                  <span
                    >Every booking arrives as a request for you to accept or decline, whatever the
                    party size.</span
                  >
                </p>
              }
              <p class="rs-hint" data-testid="party-combinations-hint">
                <i class="bx bx-merge" aria-hidden="true"></i>
                <span
                  >A party bigger than your largest table can still be booked on tables you push
                  together. Set them up in
                  <a
                    class="rs-inline-link"
                    routerLink="/main/table"
                    [queryParams]="{ view: 'combinations' }"
                    >Tables → Combinations</a
                  >; without one, such a party gets no time.</span
                >
              </p>
            </div>
          </div>

          <div class="rs-row rs-row--stack" formGroupName="window">
            <span class="rs-icon"><i class="bx bx-calendar"></i></span>
            <div class="rs-text">
              <span class="rs-row-title">Booking window</span>
              <span class="rs-row-desc"
                >Minimum notice before a slot and how far ahead guests may book.</span
              >
              <div class="rs-inline">
                <label
                  >Min notice
                  <input
                    type="number"
                    class="rs-input rs-input--sm"
                    formControlName="minNoticeMinutes"
                    min="0"
                    step="15"
                  />
                  min</label
                >
                <label
                  >Up to
                  <input
                    type="number"
                    class="rs-input rs-input--sm"
                    formControlName="maxDaysAhead"
                    min="1"
                    max="365"
                  />
                  days ahead</label
                >
              </div>
            </div>
          </div>

          @if (mode === 'request' || showsTimed) {
            <div class="rs-row">
              <span class="rs-icon"><i class="bx bx-hourglass"></i></span>
              <div class="rs-text">
                <span class="rs-row-title">Request expiry</span>
                <span class="rs-row-desc" data-testid="request-expiry-desc"
                  >How long you have to answer a request. One left unanswered is declined
                  automatically after this long (or at its booking time, if sooner), and the guest
                  is told.</span
                >
              </div>
              <div class="rs-field">
                <input
                  type="number"
                  class="rs-input"
                  formControlName="requestExpiryMinutes"
                  min="15"
                  step="15"
                /><span class="rs-unit">min</span>
              </div>
            </div>
          }
        </div>

        <!-- Booking hours: saved to the restaurant's serving hours (FNF-1181) -->
        <div class="rs-section" data-tour="booking-hours">
          <span class="rs-section-label">Booking hours</span>
          @if (hoursState === 'loading') {
            <p class="rs-hint">Loading the restaurant's hours…</p>
          } @else if (hoursState === 'failed' || !hoursForm) {
            <p class="rs-hint rs-hint--warn" role="alert">
              <i class="bx bx-error-circle" aria-hidden="true"></i>
              The restaurant's hours couldn't be loaded, so booking hours can't be changed right
              now. Reload the page to try again.
            </p>
          } @else {
            <div class="rs-row rs-row--stack" [formGroup]="hoursForm">
              <span class="rs-icon"><i class="bx bx-time-five"></i></span>
              <div class="rs-text">
                <span class="rs-row-title">When guests can book</span>
                <span class="rs-row-desc"
                  >Stop taking bookings before the kitchen closes. A booking still needs its full
                  turn time to end inside these hours.</span
                >
                <div class="rs-choice" role="radiogroup" aria-label="Booking hours">
                  <label
                    ><input type="radio" formControlName="custom" [value]="false" /> Same as opening
                    hours</label
                  >
                  <label
                    ><input type="radio" formControlName="custom" [value]="true" /> Set booking
                    hours</label
                  >
                </div>

                @if (!customHours) {
                  @if (hasOpeningHours) {
                    <p class="rs-hint">
                      Guests can book whenever the restaurant is open, as set on
                      <a class="rs-inline-link" routerLink="/main/restaurant-profile"
                        >Restaurant Profile</a
                      >.
                    </p>
                  } @else {
                    <p class="rs-hint rs-hint--warn">
                      <i class="bx bx-error-circle" aria-hidden="true"></i>
                      No opening hours are set, so no time can be booked. Set them on
                      <a class="rs-inline-link" routerLink="/main/restaurant-profile"
                        >Restaurant Profile</a
                      >, or set booking hours here.
                    </p>
                  }
                } @else {
                  <div class="rs-days" formArrayName="days">
                    @for (d of hoursDays.controls; track $index; let i = $index) {
                      <div class="rs-day" [formGroupName]="i" data-testid="booking-day">
                        <label class="rs-day-name"
                          ><input type="checkbox" formControlName="open" /> {{ d.value.day }}</label
                        >
                        <div class="rs-day-body">
                          @if (d.value.open) {
                            <div class="rs-day-ranges" formArrayName="ranges">
                              @for (r of rangesOf(i).controls; track r; let j = $index) {
                                <span class="rs-range" [formGroup]="asGroup(r)">
                                  <input
                                    type="time"
                                    class="rs-input rs-input--time"
                                    formControlName="start_time"
                                    step="900"
                                    [attr.aria-label]="d.value.day + ' bookings from'"
                                  />
                                  <span class="rs-band-label">to</span>
                                  <input
                                    type="time"
                                    class="rs-input rs-input--time"
                                    formControlName="end_time"
                                    step="900"
                                    [attr.aria-label]="d.value.day + ' bookings until'"
                                  />
                                  @if (rangesOf(i).length > 1) {
                                    <button
                                      type="button"
                                      class="rs-x"
                                      (click)="removeRange(i, j)"
                                      [attr.aria-label]="'Remove these ' + d.value.day + ' hours'"
                                    >
                                      <i class="bx bx-x"></i>
                                    </button>
                                  }
                                </span>
                              }
                              <button type="button" class="rs-link" (click)="addRange(i)">
                                <i class="bx bx-plus"></i> Add hours
                              </button>
                            </div>
                          } @else {
                            <span class="rs-day-off">No bookings</span>
                          }
                          @if (dayError(i); as err) {
                            <p class="rs-field-error" role="alert">{{ err }}</p>
                          }
                        </div>
                        <span class="rs-day-open">Open {{ openingHoursLabel(d.value.day) }}</span>
                      </div>
                    }
                  </div>
                  @if (hoursForm.hasError('noDays')) {
                    <p class="rs-field-error" role="alert">
                      Switch on at least one day. To stop bookings altogether, set the mode to Off.
                    </p>
                  } @else {
                    <p class="rs-hint">
                      An end time earlier than the start runs past midnight. A day switched off
                      takes no bookings, even when the restaurant is open.
                    </p>
                  }
                }
              </div>
            </div>
          }
        </div>

        <!-- Policy -->
        <div class="rs-section" formGroupName="policy" data-tour="policy">
          <span class="rs-section-label">Guest policy</span>
          <div class="rs-row">
            <span class="rs-icon"><i class="bx bx-x-circle"></i></span>
            <div class="rs-text">
              <span class="rs-row-title">Cancellation cutoff</span>
              <span class="rs-row-desc"
                >Guests can cancel online until this long before their time; staff always can.</span
              >
            </div>
            <div class="rs-field">
              <input
                type="number"
                class="rs-input"
                formControlName="cancelCutoffMinutes"
                min="0"
                step="15"
              /><span class="rs-unit">min</span>
            </div>
          </div>
          <div class="rs-row">
            <span class="rs-icon"><i class="bx bx-user-x"></i></span>
            <div class="rs-text">
              <span class="rs-row-title">No-show grace period</span>
              <span class="rs-row-desc"
                >After this, staff can mark the booking a no-show and release the table.</span
              >
            </div>
            <div class="rs-field">
              <input
                type="number"
                class="rs-input"
                formControlName="noShowGraceMinutes"
                min="0"
                max="120"
                step="5"
              /><span class="rs-unit">min</span>
            </div>
          </div>
          <div class="rs-row rs-row--soon">
            <span class="rs-icon"><i class="bx bx-credit-card"></i></span>
            <div class="rs-text">
              <span class="rs-row-title"
                >Deposit per guest <span class="rs-soon">coming soon</span></span
              >
              <span class="rs-row-desc"
                >Hold a card or charge a deposit for large parties. Needs payments to be
                connected.</span
              >
            </div>
            <div class="rs-field">
              <span class="rs-unit">$</span
              ><input type="number" class="rs-input" formControlName="depositCents" />
            </div>
          </div>
        </div>
      }

      @if (showsGuestSettings) {
        <!-- Closed dates and special hours (FNF-1181); the list is shared with Restaurant Hours (FNF-1612) -->
        <div class="rs-section" data-tour="closed-dates">
          <span class="rs-section-label">Closed dates &amp; special hours</span>
          @if (hoursState === 'loading') {
            <p class="rs-hint">Loading the restaurant's hours…</p>
          } @else if (hoursState === 'failed') {
            <p class="rs-hint rs-hint--warn" role="alert">
              <i class="bx bx-error-circle" aria-hidden="true"></i>
              Closed dates couldn't be loaded. Reload the page to try again.
            </p>
          } @else {
            <app-date-exceptions
              heading="Holidays and special days"
              [exceptions]="dateExceptions"
              [showSpecialHours]="true"
              [offerSpecialHours]="offersSpecialHours"
            />
          }
        </div>
      }

      @if (showsWaitlist) {
        <!-- Waitlist -->
        <div class="rs-section" formGroupName="waitlist" data-tour="waitlist">
          <span class="rs-section-label">Waitlist</span>
          <div class="rs-row">
            <span class="rs-icon"><i class="bx bx-bell"></i></span>
            <div class="rs-text">
              <span class="rs-row-title">Time to claim a table</span>
              <span class="rs-row-desc"
                >After the "table ready" text, how long before the spot goes to the next
                party.</span
              >
            </div>
            <div class="rs-field">
              <input
                type="number"
                class="rs-input"
                formControlName="notifyTimeoutMinutes"
                min="3"
                max="60"
              /><span class="rs-unit">min</span>
            </div>
          </div>
          <div class="rs-row">
            <span class="rs-icon"><i class="bx bx-brain"></i></span>
            <div class="rs-text">
              <span class="rs-row-title">Wait-time quotes</span>
              <span class="rs-row-desc"
                >Simple uses turn times and queue depth. Learned trains on your own history once
                enough is collected.</span
              >
            </div>
            <select class="rs-input rs-select" formControlName="quoteStrategy">
              <option value="simple">Simple</option>
              <option value="learned">Learned (AI)</option>
            </select>
          </div>
        </div>
      }

      @if (showsGuestSettings) {
        <!-- Notifications -->
        <div class="rs-section" formGroupName="notifications" data-tour="notifications">
          <span class="rs-section-label">Guest notifications</span>
          <div class="rs-row">
            <span class="rs-icon"><i class="bx bx-message-rounded-dots"></i></span>
            <div class="rs-text">
              <span class="rs-row-title">SMS</span>
              <span class="rs-row-desc">{{ smsDescription }}</span>
            </div>
            <label class="switch"
              ><input type="checkbox" role="switch" formControlName="sms" aria-label="SMS" /><span
                class="slider"
              ></span
            ></label>
          </div>
          <div class="rs-row">
            <span class="rs-icon"><i class="bx bx-envelope"></i></span>
            <div class="rs-text">
              <span class="rs-row-title">Email</span
              ><span class="rs-row-desc">Booking confirmation and change emails.</span>
            </div>
            <label class="switch"
              ><input
                type="checkbox"
                role="switch"
                formControlName="email"
                aria-label="Email" /><span class="slider"></span
            ></label>
          </div>
          <div class="rs-row rs-row--soon">
            <span class="rs-icon"><i class="bx bx-bell-plus"></i></span>
            <div class="rs-text">
              <span class="rs-row-title"
                >Push (Fynkle app) <span class="rs-soon">coming soon</span></span
              ><span class="rs-row-desc">For guests who booked from the app.</span>
            </div>
            <label class="switch"
              ><input type="checkbox" role="switch" formControlName="push" aria-label="Push" /><span
                class="slider"
              ></span
            ></label>
          </div>
          @if (showsReminders) {
            <div class="rs-row rs-row--stack" data-testid="reminders">
              <span class="rs-icon"><i class="bx bx-alarm"></i></span>
              <div class="rs-text">
                <span class="rs-row-title">Reminders</span>
                <span class="rs-row-desc">When to remind guests before their booking.</span>
                <div class="rs-chips" role="group" aria-label="Reminder times">
                  @for (opt of reminderOptions; track opt.minutes) {
                    <button
                      type="button"
                      class="rs-chip"
                      [class.rs-chip--on]="reminderOn(opt.minutes)"
                      [attr.aria-pressed]="reminderOn(opt.minutes)"
                      [disabled]="reminderDisabled(opt.minutes)"
                      (click)="toggleReminder(opt.minutes)"
                    >
                      @if (reminderOn(opt.minutes)) {
                        <i class="bx bx-check" aria-hidden="true"></i>
                      }
                      {{ opt.label }}
                    </button>
                  }
                </div>
                @if (remindersAtCap) {
                  <span class="rs-row-desc rs-chips-hint" data-testid="reminder-cap">
                    Up to {{ maxReminders }} reminders.
                  </span>
                }
              </div>
            </div>
          }
        </div>

        <!-- Channels -->
        <div class="rs-section" formGroupName="channels" data-tour="channels">
          <span class="rs-section-label">Booking channels</span>
          <div class="rs-row rs-row--soon" formGroupName="google">
            <span class="rs-icon"><i class="bx bxl-google"></i></span>
            <div class="rs-text">
              <span class="rs-row-title"
                >Reserve with Google <span class="rs-soon">coming soon</span></span
              >
              <span class="rs-row-desc"
                >Let diners book from Google Search and Maps. Availability is answered from the same
                tables.</span
              >
            </div>
            <label class="switch"
              ><input
                type="checkbox"
                role="switch"
                formControlName="enabled"
                aria-label="Reserve with Google" /><span class="slider"></span
            ></label>
          </div>
          <div class="rs-row rs-row--soon" formGroupName="opentable">
            <span class="rs-icon"><i class="bx bx-link-external"></i></span>
            <div class="rs-text">
              <span class="rs-row-title">OpenTable <span class="rs-soon">coming soon</span></span>
              <span class="rs-row-desc"
                >Mirror bookings to and from OpenTable once the partnership is in place.</span
              >
            </div>
            <label class="switch"
              ><input
                type="checkbox"
                role="switch"
                formControlName="enabled"
                aria-label="OpenTable" /><span class="slider"></span
            ></label>
          </div>
        </div>
      }

      <footer class="rs-foot">
        <span class="rs-hint">
          @if (hasUnsavedChanges) {
            Unsaved changes
          } @else {
            All changes saved
          }
        </span>
        <button
          type="submit"
          class="rs-save"
          [disabled]="saving || !hasUnsavedChanges"
          data-tour="save"
        >
          Save settings
        </button>
      </footer>
    </div>
  </form>
}

@if (tourOpen) {
  <fnf-tour [steps]="tourSteps" (closed)="endTour()" />
}
